import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import apiService from '../../../services/api';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Added FiUserCheck
const { FiUser, FiMessageSquare, FiSend, FiBarChart2, FiInfo, FiEdit3, FiList, FiFileText, FiUserCheck } = FiIcons;

const LABELS = {
  1: "katastrophal",
  2: "sehr schlecht",
  3: "schlecht",
  4: "Neutral",
  5: "gut",
  6: "sehr gut",
  7: "grandios"
};

// Helper Component for Slider Interaction (Adapted from Evaluation.jsx)
const QuestionSlider = ({ value, onChange }) => {
  const [localValue, setLocalValue] = useState(value || 0);

  useEffect(() => {
    setLocalValue(value || 0);
  }, [value]);

  const handleChange = (e) => {
    const newVal = parseInt(e.target.value, 10);
    setLocalValue(newVal);
    onChange(newVal);
  };

  const getBackgroundStyle = () => {
    if (!localValue || localValue === 0) return { background: '#e5e7eb' };
    
    const percentage = ((localValue - 1) / 6) * 100;
    return {
      background: `linear-gradient(to right, #152627 0%, #152627 ${percentage}%, #e5e7eb ${percentage}%, #e5e7eb 100%)`
    };
  };

  return (
    <div className="w-full max-w-3xl py-2">
      <div className="mb-0">
          <div className="flex justify-between items-end mb-1">
             <span className="text-sm font-medium text-gray-700">Bewertung:</span>
             <span className={`text-sm font-bold ${localValue > 0 ? 'text-brand-secondary' : 'text-gray-400'}`}>
                {localValue > 0 ? `${localValue} - ${LABELS[localValue]}` : 'Nicht bewertet'}
             </span>
          </div>
          
          <div className="relative h-6 flex items-center">
            <input
                type="range"
                min="1"
                max="7"
                step="1"
                value={localValue || 1}
                onChange={handleChange}
                style={getBackgroundStyle()}
                className="custom-range-slider"
            />
         </div>
      </div>
      
      <div className="flex justify-between text-[10px] sm:text-xs text-gray-400 text-center px-0">
        {Object.entries(LABELS).map(([key, label]) => (
            <div key={key} className={`flex flex-col items-center w-6 justify-start pt-1 ${localValue == key ? 'text-brand-secondary font-bold' : ''}`}>
                <span className="hidden sm:block">{key}</span>
            </div>
        ))}
      </div>
    </div>
  );
};

const TeamFeedback = ({ members }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  
  // States for feedback giving
  const [feedbackValues, setFeedbackValues] = useState({});
  const [feedbackIds, setFeedbackIds] = useState({}); // Stores IDs of existing feedback records
  const [textFeedback, setTextFeedback] = useState('');
  const [loadingMemberData, setLoadingMemberData] = useState(false);
  
  // States for feedback receiving
  const [myFeedbackStats, setMyFeedbackStats] = useState(null);
  const [receivedTextFeedbacks, setReceivedTextFeedbacks] = useState([]);
  
  const [activeTab, setActiveTab] = useState('give'); // 'give' or 'receive'
  const [giveSubTab, setGiveSubTab] = useState('ratings'); // 'ratings' or 'text'
  const [receiveSubTab, setReceiveSubTab] = useState('stats'); // 'stats' or 'texts'
  
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Filter members who have activated feedback and are not the current user
  const feedbackEnabledMembers = members.filter(
    m => m.id !== user.id && (m.feedback_active === 1 || m.feedback_active === true)
  );

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedMember) {
      loadMemberFeedback(selectedMember.id);
    } else {
      // Reset states when deselecting
      setFeedbackValues({});
      setFeedbackIds({});
      setTextFeedback('');
      setGiveSubTab('ratings');
      setMessage({ type: '', text: '' });
    }
  }, [selectedMember]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [questionsRes, myFeedbackRes, myTextFeedbackRes] = await Promise.all([
        apiService.getFeedbackQuestions(),
        apiService.getUserFeedbacks({ receiver_id: user.id }),
        apiService.getFeedbackStatements({ receiver_id: user.id })
      ]);

      if (questionsRes.status === 'success') {
        setQuestions(questionsRes.data || []);
      }

      // Calculate stats for received feedback
      if (myFeedbackRes.status === 'success' && questionsRes.status === 'success') {
        calculateStats(myFeedbackRes.data || [], questionsRes.data || []);
      }

      if (myTextFeedbackRes.status === 'success') {
        setReceivedTextFeedbacks(myTextFeedbackRes.data || []);
      }

    } catch (error) {
      console.error("Error loading feedback data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMemberFeedback = async (memberId) => {
    setLoadingMemberData(true);
    try {
      // Fetch existing ratings ONLY
      const ratingsRes = await apiService.getUserFeedbacks({ sender_id: user.id, receiver_id: memberId });

      // Process Ratings
      const initialValues = {};
      const initialIds = {};
      if (ratingsRes.status === 'success' && Array.isArray(ratingsRes.data)) {
        ratingsRes.data.forEach(r => {
          initialValues[r.feedback_id] = r.feeback_value;
          initialIds[r.feedback_id] = r.id; // Store ID for update
        });
      }
      setFeedbackValues(initialValues);
      setFeedbackIds(initialIds);
      
      // Clear text feedback input for new entry
      setTextFeedback('');

    } catch (error) {
      console.error("Error loading member feedback:", error);
      setMessage({ type: 'error', text: 'Fehler beim Laden des vorhandenen Feedbacks.' });
    } finally {
      setLoadingMemberData(false);
    }
  };

  const calculateStats = (feedbacks, questionsList) => {
    const stats = {};
    
    questionsList.forEach(q => {
      const qFeedbacks = feedbacks.filter(f => f.feedback_id === q.id);
      if (qFeedbacks.length > 0) {
        const sum = qFeedbacks.reduce((acc, curr) => acc + curr.feeback_value, 0);
        stats[q.id] = {
          question: q.question,
          average: (sum / qFeedbacks.length).toFixed(1),
          count: qFeedbacks.length
        };
      }
    });
    
    setMyFeedbackStats(stats);
  };

  const handleRatingChange = (questionId, value) => {
    setFeedbackValues(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSaveRatings = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const promises = questions.map(q => {
        const value = feedbackValues[q.id];
        if (!value) return null; // Skip questions without rating

        const existingId = feedbackIds[q.id];
        const payload = {
          feeback_value: parseInt(value),
          sender_id: user.id,
          receiver_id: String(selectedMember.id), // Note: string based on API spec/behavior
          feedback_id: parseInt(q.id)
        };

        if (existingId) {
          // Update existing
          return apiService.updateUserFeedback(existingId, payload);
        } else {
          // Create new
          return apiService.createUserFeedback(payload);
        }
      }).filter(p => p !== null);

      if (promises.length === 0) {
        setMessage({ type: 'error', text: 'Bitte geben Sie mindestens eine Bewertung ab.' });
        setSubmitting(false);
        return;
      }

      await Promise.all(promises);
      
      setMessage({ type: 'success', text: 'Bewertungen erfolgreich gespeichert!' });
      // Reload to get new IDs
      await loadMemberFeedback(selectedMember.id);
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);

    } catch (error) {
      console.error("Error saving ratings:", error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Bewertungen.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveText = async (e) => {
    e.preventDefault();
    if (!selectedMember) return;
    if (!textFeedback.trim()) {
      setMessage({ type: 'error', text: 'Bitte geben Sie einen Text ein.' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        text: textFeedback,
        sender_id: user.id,
        receiver_id: selectedMember.id // Note: integer based on API spec/behavior
      };

      // Always create new feedback statement (allow multiple)
      await apiService.createFeedbackStatement(payload);

      setMessage({ type: 'success', text: 'Text-Feedback erfolgreich gesendet!' });
      setTextFeedback(''); // Clear input after sending
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);

    } catch (error) {
      console.error("Error saving text feedback:", error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern des Text-Feedbacks.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div></div>;
  }

  // Check if current user has enabled feedback
  if (!user.feedback_active && user.feedback_active !== 1) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <SafeIcon icon={FiInfo} className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-yellow-800 mb-2">Feedback nicht aktiviert</h3>
        <p className="text-yellow-700 mb-4">
          Um die Feedback-Funktion zu nutzen (Feedback geben und erhalten), müssen Sie dies in Ihrem Profil aktivieren.
        </p>
        <a href="#/profile" className="inline-block px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors">
          Zum Profil
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[500px]">
      {/* Main Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('give')}
            className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
              activeTab === 'give'
                ? 'border-brand-secondary text-brand-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center">
              {/* Updated icon to FiUserCheck */}
              <SafeIcon icon={FiUserCheck} className="mr-2 w-5 h-5" />
              Feedback geben
            </div>
          </button>
          <button
            onClick={() => setActiveTab('receive')}
            className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
              activeTab === 'receive'
                ? 'border-brand-secondary text-brand-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-center">
              <SafeIcon icon={FiBarChart2} className="mr-2 w-5 h-5" />
              Mein Feedback
            </div>
          </button>
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'give' ? (
          <div>
            {!selectedMember ? (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Wählen Sie ein Teammitglied</h3>
                {feedbackEnabledMembers.length === 0 ? (
                  <p className="text-gray-500">Keine anderen Teammitglieder haben die Feedback-Funktion aktiviert.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {feedbackEnabledMembers.map(member => (
                      <button
                        key={member.id}
                        onClick={() => setSelectedMember(member)}
                        className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-brand-primary transition-all text-left group"
                      >
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold mr-3 group-hover:bg-brand-secondary group-hover:text-white transition-colors">
                          <SafeIcon icon={FiUser} className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{member.name}</div>
                          <div className="text-xs text-gray-500">Feedback geben</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <button 
                  onClick={() => setSelectedMember(null)}
                  className="mb-6 text-sm text-gray-500 hover:text-gray-700 flex items-center"
                >
                  ← Zurück zur Übersicht
                </button>

                <div className="mb-6 pb-4 border-b border-gray-100 flex justify-between items-end">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 flex items-center">
                      Feedback für <span className="text-brand-secondary ml-2">{selectedMember.name}</span>
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {giveSubTab === 'ratings' 
                        ? 'Ihre Bewertungen werden aktualisiert/überschrieben.' 
                        : 'Sie können mehrere Text-Nachrichten senden.'}
                    </p>
                  </div>
                </div>

                {message.text && (
                  <div className={`mb-6 p-4 rounded-md ${message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {message.text}
                  </div>
                )}

                {loadingMemberData ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    {/* Give Sub-Tabs */}
                    <div className="flex border-b border-gray-200 bg-white">
                      <button
                        onClick={() => setGiveSubTab('ratings')}
                        className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors flex items-center justify-center ${
                          giveSubTab === 'ratings'
                            ? 'bg-gray-50 text-brand-secondary border-b-2 border-brand-secondary'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <SafeIcon icon={FiList} className="mr-2 w-4 h-4" />
                        Fragen & Bewertung
                      </button>
                      <button
                        onClick={() => setGiveSubTab('text')}
                        className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors flex items-center justify-center ${
                          giveSubTab === 'text'
                            ? 'bg-gray-50 text-brand-secondary border-b-2 border-brand-secondary'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <SafeIcon icon={FiEdit3} className="mr-2 w-4 h-4" />
                        Textuelles Feedback
                      </button>
                    </div>

                    <div className="p-6">
                      {giveSubTab === 'ratings' && (
                        <form onSubmit={handleSaveRatings}>
                          <div className="space-y-6 mb-6">
                            {questions.length === 0 ? (
                              <p className="text-gray-500 text-center py-4">Keine Fragen verfügbar.</p>
                            ) : (
                              questions.map(q => (
                                <div key={q.id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                                  <label className="block text-sm font-medium text-gray-900 mb-3">
                                    {q.question}
                                  </label>
                                  
                                  {/* Replaced star rating with QuestionSlider */}
                                  <QuestionSlider 
                                    value={feedbackValues[q.id]}
                                    onChange={(val) => handleRatingChange(q.id, val)}
                                  />
                                  
                                </div>
                              ))
                            )}
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="submit"
                              disabled={submitting}
                              className="flex items-center px-6 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <SafeIcon icon={FiSend} className="mr-2 w-4 h-4" />
                              {submitting ? 'Speichere...' : 'Bewertungen speichern'}
                            </button>
                          </div>
                        </form>
                      )}

                      {giveSubTab === 'text' && (
                        <form onSubmit={handleSaveText}>
                          <div className="mb-6">
                            <label className="block text-sm font-medium text-gray-900 mb-2">
                              Persönliches Feedback
                            </label>
                            <div className="relative">
                              <textarea
                                value={textFeedback}
                                onChange={(e) => setTextFeedback(e.target.value)}
                                rows={8}
                                className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary shadow-sm resize-none"
                                placeholder="Was schätzen Sie an der Zusammenarbeit? Wo sehen Sie Verbesserungspotenzial?"
                              />
                              <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                                {textFeedback.length} Zeichen
                              </div>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              Dieses Feedback ist für den Empfänger sichtbar (anonymisiert oder direkt, je nach Kontext).
                            </p>
                          </div>
                          <div className="flex justify-end">
                            <button
                              type="submit"
                              disabled={submitting}
                              className="flex items-center px-6 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-dark transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <SafeIcon icon={FiSend} className="mr-2 w-4 h-4" />
                              {submitting ? 'Sende...' : 'Text senden'}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Receive Tab Content */
          <div>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Meine Feedback-Auswertung</h3>
            </div>

            <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden mb-6">
              {/* Receive Sub-Tabs */}
              <div className="flex border-b border-gray-200 bg-white">
                <button
                  onClick={() => setReceiveSubTab('stats')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors flex items-center justify-center ${
                    receiveSubTab === 'stats'
                      ? 'bg-gray-50 text-brand-secondary border-b-2 border-brand-secondary'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={FiBarChart2} className="mr-2 w-4 h-4" />
                  Bewertete Fragen
                </button>
                <button
                  onClick={() => setReceiveSubTab('texts')}
                  className={`flex-1 py-3 px-4 text-center font-medium text-sm transition-colors flex items-center justify-center ${
                    receiveSubTab === 'texts'
                      ? 'bg-gray-50 text-brand-secondary border-b-2 border-brand-secondary'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={FiFileText} className="mr-2 w-4 h-4" />
                  Textuelles Feedback
                </button>
              </div>

              <div className="p-6">
                {receiveSubTab === 'stats' && (
                  <>
                    {!myFeedbackStats || Object.keys(myFeedbackStats).length === 0 ? (
                      <div className="text-center py-12">
                        <SafeIcon icon={FiBarChart2} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Noch keine Bewertungen erhalten.</p>
                      </div>
                    ) : (
                      <div className="grid gap-6">
                        {Object.values(myFeedbackStats).map((stat, index) => (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium text-gray-900 pr-4">{stat.question}</h4>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {stat.count} Bewertungen
                              </span>
                            </div>
                            <div className="flex items-center mt-2">
                              <div className="flex-1 mr-4">
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-brand-secondary rounded-full" 
                                    style={{ width: `${(stat.average / 7) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                              <div className="text-2xl font-bold text-brand-dark w-12 text-right">
                                {stat.average}
                              </div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                              <span>1.0</span>
                              <span>7.0</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-8 p-4 bg-blue-50 rounded-md text-sm text-blue-800 flex items-start">
                      <SafeIcon icon={FiInfo} className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                      <p>Diese Werte basieren auf anonymen Bewertungen Ihrer Teammitglieder.</p>
                    </div>
                  </>
                )}

                {receiveSubTab === 'texts' && (
                  <>
                    {receivedTextFeedbacks.length === 0 ? (
                      <div className="text-center py-12">
                        <SafeIcon icon={FiMessageSquare} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">Noch kein textuelles Feedback erhalten.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {receivedTextFeedbacks.map((item, index) => (
                          <div key={item.id || index} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                            <div className="flex items-start">
                              <div className="flex-shrink-0 mr-4">
                                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                                  <SafeIcon icon={FiUser} className="w-6 h-6" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                  Anonymes Feedback
                                </div>
                                <div className="text-gray-700 whitespace-pre-wrap">
                                  {item.text}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamFeedback;